import { useCallback, useRef, useState } from "react";
import {
  streamBenchmarkRun,
  type BenchmarkCaseInput,
  type BenchmarkRunRequestPayload,
  type CaseResultEvent,
  type CaseStatusUpdateEvent,
  type RunCompleteEvent,
  type StoredBenchmarkRun,
} from "@financial-advisory/data-access/financial-advisory-api-service";
import type {
  BenchmarkCase,
  BenchmarkMetrics,
  CaseRunResult,
  CaseStatus,
} from "./types";

export const DEFAULT_MAX_MESSAGES = 50;
export const DEFAULT_MAX_CONCURRENT = 5;

interface BenchmarkRunnerState {
  isRunning: boolean;
  caseStatuses: Map<number, CaseStatus>;
  caseMessageCounts: Map<number, number>;
  caseSessionIds: Map<number, string>;
  results: CaseRunResult[];
  currentCaseIndex: number;
  totalCases: number;
}

interface BenchmarkRunResult {
  results: CaseRunResult[];
  metrics: BenchmarkMetrics;
  storedRun: StoredBenchmarkRun;
}

function toBenchmarkCaseInput(c: BenchmarkCase): BenchmarkCaseInput {
  const def = c.modifiedCase;
  return {
    id: c.id,
    case_definition: {
      situation: def.situation,
      segment: def.segment,
      situation_summary: def.situationSummary,
      expected_suggestion: def.expectedSuggestion,
      persona_detail: def.personaDetail,
    },
  };
}

export function useBenchmarkRunner() {
  const [state, setState] = useState<BenchmarkRunnerState>({
    isRunning: false,
    caseStatuses: new Map(),
    caseMessageCounts: new Map(),
    caseSessionIds: new Map(),
    results: [],
    currentCaseIndex: 0,
    totalCases: 0,
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  const run = useCallback(
    (
      cases: BenchmarkCase[],
      maxConcurrent: number,
      maxMessages: number = DEFAULT_MAX_MESSAGES,
      shouldTerminateEarlyOnRecommendation = false,
    ): Promise<BenchmarkRunResult | null> => {
      const allCaseIds = cases.map((c) => c.id);
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setState({
        isRunning: true,
        caseStatuses: new Map(
          allCaseIds.map((id) => [id, "pending" as CaseStatus]),
        ),
        caseMessageCounts: new Map(),
        caseSessionIds: new Map(),
        results: [],
        currentCaseIndex: 0,
        totalCases: allCaseIds.length,
      });

      const payload: BenchmarkRunRequestPayload = {
        cases: cases.map(toBenchmarkCaseInput),
        max_concurrent: maxConcurrent,
        max_messages: maxMessages,
        should_terminate_early: shouldTerminateEarlyOnRecommendation,
      };

      return new Promise<BenchmarkRunResult | null>((resolve) => {
        let resolved = false;
        const safeResolve = (value: BenchmarkRunResult | null) => {
          if (resolved) return;
          resolved = true;
          resolve(value);
        };

        const handleCaseStatusUpdate = (event: CaseStatusUpdateEvent) => {
          setState((prev) => ({
            ...prev,
            caseStatuses: new Map(prev.caseStatuses).set(
              event.case_id,
              event.status as CaseStatus,
            ),
            caseMessageCounts: new Map(prev.caseMessageCounts).set(
              event.case_id,
              event.message_count,
            ),
            ...(event.session_id
              ? {
                caseSessionIds: new Map(prev.caseSessionIds).set(
                  event.case_id,
                  event.session_id,
                ),
              }
              : {}),
          }));
        };

        const handleCaseResult = (event: CaseResultEvent) => {
          const result: CaseRunResult = {
            caseId: event.case_id,
            sessionId: event.session_id,
            sessionVersionId: event.session_version_id,
            status: event.status as CaseStatus,
            messageCount: event.message_count,
            error: event.error,
            actualSuggestions: event.actual_suggestions,
            isPass: event.is_pass,
            expectsNone: event.expects_none,
          };

          setState((prev) => ({
            ...prev,
            caseStatuses: new Map(prev.caseStatuses).set(
              event.case_id,
              event.status as CaseStatus,
            ),
            results: [...prev.results, result],
            currentCaseIndex: prev.results.length + 1,
          }));
        };

        const handleRunComplete = (event: RunCompleteEvent) => {
          const finalResults: CaseRunResult[] = event.results.map((r) => ({
            caseId: r.caseId,
            sessionId: r.sessionId,
            sessionVersionId: r.sessionVersionId,
            status: r.status as CaseStatus,
            messageCount: r.messageCount,
            error: r.error,
            actualSuggestions: r.actualSuggestions,
            isPass: r.isPass,
            expectsNone: r.expectsNone,
          }));

          setState((prev) => ({
            ...prev,
            isRunning: false,
            results: finalResults,
          }));

          safeResolve({
            results: finalResults,
            metrics: event.metrics,
            storedRun: event.stored_run,
          });
        };

        const handleError = (error: Error) => {
          setState((prev) => ({ ...prev, isRunning: false }));
          safeResolve(null);
        };

        const handleStreamEnd = () => {
          setState((prev) => ({ ...prev, isRunning: false }));
          safeResolve(null);
        };

        streamBenchmarkRun(
          payload,
          {
            onCaseStatusUpdate: handleCaseStatusUpdate,
            onCaseResult: handleCaseResult,
            onRunComplete: handleRunComplete,
            onError: handleError,
            onStreamEnd: handleStreamEnd,
          },
          abortController.signal,
        );
      });
    },
    [],
  );

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort();
    setState((prev) => ({ ...prev, isRunning: false }));
  }, []);

  return {
    ...state,
    run,
    cancel,
  };
}
